<script lang="ts" setup>
import { DrugEventsViewer } from '@sancare/typescript-commons'
import { HealthConstantsViewer } from '@sancare/typescript-commons'
import { LabResultsViewer } from '@sancare/typescript-commons'
import { AnnotationsViewer } from '@sancare/typescript-commons'
import {
  DocumentSourceIdentifier,
  HealthDataDocument,
  HealthDataType,
  TextualHealthEntryType,
} from '@sancare/typescript-commons/src/types/health-data'
import _ from 'lodash'
import { computed, ref } from 'vue'

import ReportViewer from '@/stay-displayer/health-data/ReportDisplayer.vue'
import TextualHealthEntriesViewer from '@/stay-displayer/health-data/TextualHealthEntries.vue'
import { TextualHealthEntry } from '@/stay-displayer/health-data/types'
import { BooleanMatches, CodingSelection, type SelectedChunks } from '@/stay-displayer/types'
import { useStore } from '@/store'
import { Stay } from '@/store/modules/stay/types'

const store = useStore()

const props = withDefaults(defineProps<{
  currentStay: Stay
  currentDocument: HealthDataDocument
  selection: CodingSelection | null
  selectedChunks: SelectedChunks | null
  scrollTarget?: { docId: number, offset: number } | null
  enableFalseJustificationFeatures?: boolean
}>(), {
  scrollTarget: null,
  // Signalling a false justification directly from the document is redundant with the same feature
  // in the justification popin (ApiJustificationSummary.vue), which already handles it — disabled here
  // until BUILD-733's follow-up ticket settles where the underlying data should live long-term.
  enableFalseJustificationFeatures: false
})

const emit = defineEmits(['disable-justification-scrolling'])

const hasHealthData = computed(() => {
  return [
    props.currentStay.reports.filter((r) => r.content),
    props.currentStay.textualHealthEntries.filter((t) => t.content),
    props.currentStay.categoricalLabResults.filter((c) => c.value),
    props.currentStay.healthConstants,
    props.currentStay.labResults,
    props.currentStay.drugEvents,
    props.currentStay.annotations
  ].flat().length > 0
})

function getStructuredActiveIds(boolChunks: BooleanMatches): number[] {
  const activeIds = []
  for (const id in boolChunks) {
    if (boolChunks[id]) {
      activeIds.push(Number(id))
    }
  }
  return activeIds
}

const activeDrugEventsIds = computed(() => getStructuredActiveIds(props.selectedChunks?.drugEvents ?? {}))
const activeHealthConstantIds = computed(() => getStructuredActiveIds(props.selectedChunks?.healthConstants ?? {}))
const activeLabResultIds = computed(() => getStructuredActiveIds(props.selectedChunks?.labResults ?? {}))
const activeCategoricalLabResultIds = computed(() => getStructuredActiveIds(props.selectedChunks?.categoricalLabResults ?? {}))

const isDocumentSource = ref(false)

function setCurrentDocument(documentSource: DocumentSourceIdentifier) {
  let type = documentSource.type

  if (documentSource.type === HealthDataType.TEXTUAL_HEALTH_ENTRY) {
    type = _.find(
      props.currentStay.textualHealthEntries,
      (textualHealthEntry: TextualHealthEntry) => textualHealthEntry.id === documentSource.id
    ).type
  }

  store.commit('setMainContent', {
    type: type,
    targetId: documentSource.id
  })
  isDocumentSource.value = true
}

</script>

<template>
  <div class="card report">
    <div
      v-if="!hasHealthData"
      class="text-center"
    >
      Aucun document concernant ce séjour
    </div>
    <report-viewer
      v-else-if="currentDocument.type === HealthDataType.REPORT"
      :report="currentStay.reports
        .find((item) => currentDocument.targetId === item.id)"
      :selection="selection"
      :scroll-target="scrollTarget"
      :is-document-source="isDocumentSource"
      :enable-false-justification-features="enableFalseJustificationFeatures"
      @disable-justification-scrolling="emit('disable-justification-scrolling')"
    />
    <drug-events-viewer
      v-else-if="currentDocument.type === HealthDataType.DRUG_EVENT"
      :drug-events="currentStay.drugEvents
        .filter((item) => item.prestationType === currentDocument.drugEventPrestationType)"
      :active-ids="activeDrugEventsIds"
      :drug-event-prestation-type="currentDocument.drugEventPrestationType"
    />
    <health-constants-viewer
      v-else-if="currentDocument.type === HealthDataType.HEALTH_CONSTANT"
      :health-constants="currentStay.healthConstants"
      :active-ids="activeHealthConstantIds"
      @show-source="(documentSource: DocumentSourceIdentifier) => setCurrentDocument(documentSource)"
    />
    <lab-results-viewer
      v-else-if="currentDocument.type === HealthDataType.LAB_RESULT"
      :numerical-lab-results="currentStay.labResults"
      :categorical-lab-results="currentStay.categoricalLabResults
        .filter((item) => item.value)"
      :active-lab-result-ids="activeLabResultIds"
      :active-categorical-lab-result-ids="activeCategoricalLabResultIds"
      @show-source="(documentSource: DocumentSourceIdentifier) => setCurrentDocument(documentSource)"
    />
    <annotations-viewer
      v-else-if="currentDocument.type === HealthDataType.ANNOTATION"
      :annotations="currentStay.annotations"
      :highlighted-annotation-ids="[]"
      @show-source="(documentSource: DocumentSourceIdentifier) => setCurrentDocument(documentSource)"
    />
    <textual-health-entries-viewer
      v-else-if="currentDocument.type === TextualHealthEntryType.NURSE_REPORT"
      :textual-health-entries="currentStay.textualHealthEntries
        .filter((item) => item.type === TextualHealthEntryType.NURSE_REPORT && item.content)"
      :selection="selection"
      :scroll-target="scrollTarget"
      :type="TextualHealthEntryType.NURSE_REPORT"
      :is-document-source="isDocumentSource"
      :enable-false-justification-features="enableFalseJustificationFeatures"
      @disable-justification-scrolling="emit('disable-justification-scrolling')"
    />
    <textual-health-entries-viewer
      v-else-if="currentDocument.type === TextualHealthEntryType.TARGET_NURSE_REPORT"
      :textual-health-entries="currentStay.textualHealthEntries
        .filter((item) => item.type === TextualHealthEntryType.TARGET_NURSE_REPORT && item.content)"
      :selection="selection"
      :scroll-target="scrollTarget"
      :type="TextualHealthEntryType.TARGET_NURSE_REPORT"
      :is-document-source="isDocumentSource"
      :enable-false-justification-features="enableFalseJustificationFeatures"
      @disable-justification-scrolling="emit('disable-justification-scrolling')"
    />
    <textual-health-entries-viewer
      v-else-if="currentDocument.type === TextualHealthEntryType.MEDICAL_HISTORY"
      :textual-health-entries="currentStay.textualHealthEntries
        .filter((item) => item.type === TextualHealthEntryType.MEDICAL_HISTORY && item.content)"
      :selection="selection"
      :scroll-target="scrollTarget"
      :type="TextualHealthEntryType.MEDICAL_HISTORY"
      :is-document-source="isDocumentSource"
      :enable-false-justification-features="enableFalseJustificationFeatures"
      @disable-justification-scrolling="emit('disable-justification-scrolling')"
    />
    <textual-health-entries-viewer
      v-else-if="currentDocument.type === TextualHealthEntryType.LAB_RESULT_COMMENT"
      :textual-health-entries="currentStay.textualHealthEntries
        .filter((item) => item.type === TextualHealthEntryType.LAB_RESULT_COMMENT && item.content)"
      :selection="selection"
      :scroll-target="scrollTarget"
      :type="TextualHealthEntryType.LAB_RESULT_COMMENT"
      :is-document-source="isDocumentSource"
      :enable-false-justification-features="enableFalseJustificationFeatures"
      @disable-justification-scrolling="emit('disable-justification-scrolling')"
    />
    <textual-health-entries-viewer
      v-else-if="currentDocument.type === TextualHealthEntryType.SURGERY_LABEL"
      :textual-health-entries="currentStay.textualHealthEntries
        .filter((item) => item.type === TextualHealthEntryType.SURGERY_LABEL && item.content)"
      :selection="selection"
      :scroll-target="scrollTarget"
      :type="TextualHealthEntryType.SURGERY_LABEL"
      :is-document-source="isDocumentSource"
      :enable-false-justification-features="enableFalseJustificationFeatures"
      @disable-justification-scrolling="emit('disable-justification-scrolling')"
    />
    <div
      v-else
      class="text-center"
    >
      Type de document "<strong>{{ currentDocument.type }}</strong>" inconnu
    </div>
  </div>
</template>
